import { Component, EventEmitter, Output, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { catchError, EMPTY, finalize, from, mergeMap, Observable, Subject, Subscription, takeUntil, tap } from 'rxjs';
import { DocumentTemplateService } from '../../services/document-template.service';
import { OcrResultComponent } from '../ocr-result/ocr-result.component';
import { DocumentMetadata } from '../../types/document-metadata';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MAX_UPLOAD_BYTES, MAX_CONCURRENT_UPLOADS, formatBytes } from '../../utils/upload.constants';
import {
  DOCUMENT_ACCEPT,
  DocumentSourceKind,
  previewKindForFile,
  supportedFormatsText,
  unsupportedFormatMessage
} from '../../utils/document-formats';

export interface OcrDocumentItem {
  file: File;
  /** Raw blob URL, retained solely so it can be revoked — a SafeResourceUrl cannot be unwrapped. */
  objectUrl: string | null;
  previewUrl: SafeResourceUrl | null;
  previewKind: DocumentSourceKind;
  /** False for a file refused at selection (unsupported format or over the size limit), which can never be started. */
  accepted: boolean;
  extractedData: DocumentMetadata | null;
  isProcessing: boolean;
  phase: 'idle' | 'queued' | 'uploading' | 'processing' | 'done' | 'error';
  uploadProgress: number;
  errorMessage: string | null;
}

@Component({
  selector: 'app-ocr-dialog',
  templateUrl: './ocr-dialog.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, OcrResultComponent, MatDialogModule]
})
export class OcrDialogComponent implements OnDestroy {
  @Output() close = new EventEmitter<void>();

  documents: OcrDocumentItem[] = [];

  readonly accept = DOCUMENT_ACCEPT;
  readonly supportedFormats = supportedFormatsText();

  private readonly work = new Subscription();

  /**
   * Per-document cancellation. Kept off `OcrDocumentItem` so the template contract stays
   * data-only, and keyed by identity so a removed document's stream can be stopped
   * without touching its siblings.
   */
  private readonly cancellations = new Map<OcrDocumentItem, Subject<void>>();

  constructor(
    private sanitizer: DomSanitizer,
    private documentTemplateService: DocumentTemplateService,
    private dialogRef: MatDialogRef<OcrDialogComponent>
  ) {}

  /**
   * The dialog is closable by ESC and backdrop click, which destroy the component
   * without `closeDialog()` running — so cleanup has to live here or every blob URL and
   * polling interval outlives the dialog.
   */
  ngOnDestroy(): void {
    this.work.unsubscribe();
    this.documents.forEach(doc => this.discard(doc));
  }

  closeDialog(): void {
    this.close.emit();
    this.dialogRef.close();
  }

  onFileSelected(event: Event): void {
    const element = event.currentTarget as HTMLInputElement;
    const fileList: FileList | null = element.files;
    if (fileList && fileList.length > 0) {
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        const newItem: OcrDocumentItem = {
          file: file,
          objectUrl: null,
          previewUrl: null,
          previewKind: previewKindForFile(file.name),
          accepted: false,
          extractedData: null,
          isProcessing: false,
          phase: 'idle',
          uploadProgress: 0,
          errorMessage: null
        };

        // By name, not MIME type: browsers report types inconsistently, and the
        // extension is what the backend decides the format by.
        const unsupported = unsupportedFormatMessage(file.name);
        if (unsupported) {
          newItem.errorMessage = unsupported;
          newItem.phase = 'error';
        } else if (file.size > MAX_UPLOAD_BYTES) {
          newItem.errorMessage = `File exceeds the ${formatBytes(MAX_UPLOAD_BYTES)} upload limit.`;
          newItem.phase = 'error';
        } else {
          newItem.accepted = true;
          // Browsers cannot render a `download` kind, so it gets a file card instead of a blob preview.
          if (newItem.previewKind !== 'download') {
            const objectUrl = URL.createObjectURL(file);
            newItem.objectUrl = objectUrl;
            newItem.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl);
          }
        }
        this.documents.push(newItem);
      }
      element.value = ''; // Reset file input so same file can be selected again
    }
  }

  startOcrProcessing(index: number): void {
    const doc = this.documents[index];
    if (!doc) return;

    const work$ = this.uploadAndProcess$(doc);
    if (work$) {
      this.work.add(work$.subscribe());
    }
  }

  /** Most browsers cannot decode a TIFF, so such an image is shown as a file card; its blob URL stays for revocation. */
  onImagePreviewError(doc: OcrDocumentItem): void {
    doc.previewKind = 'download';
  }

  startAllOcrProcessing(): void {
    // Only untouched documents: a filter based on isProcessing/errorMessage alone would
    // re-upload everything that already finished.
    const pending = this.documents.filter(doc => doc.phase === 'idle');

    // Claimed here rather than in uploadAndProcess$, because mergeMap defers the
    // projection until a slot frees — documents still waiting would otherwise stay
    // `idle` and be enqueued a second time by another click, uploading them twice.
    pending.forEach(doc => {
      doc.phase = 'queued';
      doc.isProcessing = true;
    });

    this.work.add(
      from(pending).pipe(
        mergeMap(doc => this.uploadAndProcess$(doc) ?? EMPTY, MAX_CONCURRENT_UPLOADS)
      ).subscribe()
    );
  }

  /**
   * Returns the upload+poll stream for one document without subscribing to it, so
   * `mergeMap` can actually limit concurrency — a method that subscribed internally
   * would start every upload at once and the limit would be silently ignored.
   *
   * Failures are recorded on the document and then swallowed: an error escaping here
   * would reach `mergeMap`, which forwards it to the destination and tears down every
   * other in-flight upload, stranding those documents mid-progress.
   */
  private uploadAndProcess$(doc: OcrDocumentItem): Observable<DocumentMetadata> | null {
    if (!doc.file) return null;

    // The retry buttons in the result pane are never disabled, so the limit is enforced here too.
    if (doc.file.size > MAX_UPLOAD_BYTES) {
      doc.errorMessage = `File exceeds the ${formatBytes(MAX_UPLOAD_BYTES)} upload limit.`;
      doc.phase = 'error';
      doc.isProcessing = false;
      return null;
    }

    doc.isProcessing = true;
    doc.errorMessage = null;
    doc.extractedData = null;
    doc.phase = 'uploading';
    doc.uploadProgress = 0;

    const cancel = new Subject<void>();
    this.cancellations.set(doc, cancel);

    return this.documentTemplateService
      .processDocument(doc.file, {
        onProgress: percent => { doc.uploadProgress = percent; },
        // Only the 2xx means the object exists; a full progress bar does not.
        onComplete: () => { doc.phase = 'processing'; },
      })
      .pipe(
        // Frees the mergeMap slot as well, so removing a queued document lets the next
        // one start rather than leaving the batch a worker short.
        takeUntil(cancel),
        finalize(() => this.cancellations.delete(doc)),
        tap({
          next: response => {
            doc.extractedData = response;
            // The poll emits the terminal item and then completes, so the outcome has to
            // be read here — `complete` cannot tell done from error.
            if (response.status === 'done') {
              doc.phase = 'done';
            } else if (response.status === 'error') {
              doc.phase = 'error';
              doc.errorMessage = response.errorMessage ?? 'Processing failed.';
            } else {
              doc.phase = 'processing';
            }
          },
          error: err => {
            console.error('Error processing document:', err);
            // A 400 is the backend refusing this file, and its message already says why.
            doc.errorMessage = err.status === 400 && err.error?.message
              ? err.error.message
              : `Error: ${err.error?.message || err.message || 'Failed to process document.'}`;
            doc.phase = 'error';
            doc.isProcessing = false;
          },
          complete: () => { doc.isProcessing = false; },
        }),
        catchError(() => EMPTY)
      );
  }

  removeDocument(index: number): void {
    const [removed] = this.documents.splice(index, 1);
    if (removed) {
      this.discard(removed);
    }
  }

  clearAll(): void {
    this.documents.forEach(doc => this.discard(doc));
    this.documents = [];
  }

  /**
   * Stops a document's upload and poll before dropping it. Without this the XHR keeps
   * pushing the body and the poll keeps running every two seconds against an item
   * nothing renders any more.
   */
  private discard(doc: OcrDocumentItem): void {
    const cancel = this.cancellations.get(doc);
    cancel?.next();
    cancel?.complete();

    if (doc.objectUrl) {
      URL.revokeObjectURL(doc.objectUrl);
    }
  }
}
